import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { AutoFixHigh as MakeoverIcon, CheckCircle as CheckIcon, ZoomIn as ZoomInIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import useWebotServer from "hooks/useWebotServer";
import { db } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import LightboxModal from "components/LightboxModal";
import {
  CoinBalanceBreakdown,
  GeneratedUserCreative,
  GENERATED_USER_CREATIVES_COLLECTION_NAME,
  getMakeoverCoinCost,
  resolveMakeoverEngine,
  DEFAULT_MAKEOVER_ENGINE,
  DEFAULT_MAKEOVER_QUALITY,
  MakeoverSceneType,
  REST_schema
} from "../../../@types/sharedSchema";
import useCoinBalance from "hooks/useCoinBalance";
import useCreativePreferences from "hooks/useCreativePreferences";
import CoinBalanceDisplay from "../creatives-generator/CoinBalanceDisplay";
import { ListingTitleLink } from "./ListingTitleLink";
import { MakeoverCard } from "./MakeoverCard";
import { MakeoverComparisonDialog, ComparisonPair } from "./MakeoverComparisonDialog";
import { MakeoverIntensitySelector } from "./MakeoverIntensitySelector";
import { MakeoverModelSelector } from "./MakeoverModelSelector";
import { DEFAULT_MAKEOVER_INTENSITY } from "./makeoverIntensity";

const MAX_SELECTION = 5;

type StylePreset = { key: string; labelKey: string; labelDefault: string; stylePrompt: string };

const STYLE_PRESETS: StylePreset[] = [
  { key: "modern", labelKey: "makeovers.style.modern", labelDefault: "מודרני", stylePrompt: "modern contemporary design" },
  { key: "scandinavian", labelKey: "makeovers.style.scandinavian", labelDefault: "סקנדינבי", stylePrompt: "bright Scandinavian minimal design with light woods" },
  { key: "warm", labelKey: "makeovers.style.warm", labelDefault: "חמים", stylePrompt: "warm Mediterranean style with natural textures" },
  { key: "luxury", labelKey: "makeovers.style.luxury", labelDefault: "יוקרתי", stylePrompt: "high-end luxury design with premium materials" },
  { key: "minimal", labelKey: "makeovers.style.minimal", labelDefault: "מינימליסטי", stylePrompt: "clean minimalist design, uncluttered" }
];

type MakeoverStudioProps = {
  listingId: string;
  images: string[];
  /** Render the token-balance card inside the studio. Default true.
   *  Pass false when a parent page already shows a page-level balance card. */
  showBalance?: boolean;
  /** Externally-managed balance (keeps a page-level card in sync). When omitted,
   *  the studio fetches its own — e.g. inside the listing-details dialog tab. */
  externalBalance?: CoinBalanceBreakdown | null;
  externalBalanceLoading?: boolean;
  onBalanceChange?: () => void;
};

export function MakeoverStudio({
  listingId,
  images,
  showBalance = true,
  externalBalance,
  externalBalanceLoading,
  onBalanceChange
}: MakeoverStudioProps): JSX.Element {
  const { t: translate } = useTranslation();
  const { user } = useAuth();
  const { isAdminOfTheOrganization } = useFirestore();
  // Use the parent-supplied balance when given (so the page-level card stays in
  // sync); otherwise fall back to a self-managed fetch.
  const ownBalance = useCoinBalance();
  const balance = externalBalance !== undefined ? externalBalance : ownBalance.balance;
  const balanceLoading = externalBalanceLoading !== undefined ? externalBalanceLoading : ownBalance.loading;
  const refetchBalance = onBalanceChange ?? ownBalance.refetch;
  const { generateMakeovers, setMakeoverShowcase, getWebotListingMakeovers } = useWebotServer();
  const email = user?.email as string | undefined;

  const [allCreatives, setAllCreatives] = useState<GeneratedUserCreative[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  // Engine / quality / intensity are remembered per creative type in localStorage so the
  // studio re-opens with the user's last choices (default: standard engine · 1K).
  const [prefs, updatePrefs] = useCreativePreferences("makeover", {
    engine: DEFAULT_MAKEOVER_ENGINE,
    quality: DEFAULT_MAKEOVER_QUALITY,
    intensity: DEFAULT_MAKEOVER_INTENSITY
  });
  const { quality, intensity } = prefs;
  // A preference saved before "value" was merged into "standard" would leave no engine selected.
  const engine = resolveMakeoverEngine(prefs.engine);
  const makeoverCost = getMakeoverCoinCost(engine, quality); // price for ONE image at the current engine × quality
  const [selectedStyleKeys, setSelectedStyleKeys] = useState<string[]>([]);
  const [customActive, setCustomActive] = useState(false);
  const [customStyle, setCustomStyle] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showcaseOn, setShowcaseOn] = useState(false);
  const [shareWithOrg, setShareWithOrg] = useState(false);
  const [showcaseBusy, setShowcaseBusy] = useState(false);
  const [sourceLightbox, setSourceLightbox] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
  const [resultDialog, setResultDialog] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });

  // One global listener (same query as the creatives page) feeds this listing's
  // makeover results — no extra Firestore index needed.
  useEffect(() => {
    if (!email) return;
    const unsubscribe = db
      .collection(GENERATED_USER_CREATIVES_COLLECTION_NAME)
      .where("userId", "==", email)
      .withConverter(GenericConverter<GeneratedUserCreative>())
      .onSnapshot(
        (snapshot) => setAllCreatives(snapshot.docs.map((d) => d.data())),
        (err) => console.error("Error listening to makeovers", err)
      );
    return unsubscribe;
  }, [email]);

  const listingMakeovers = useMemo(
    () =>
      allCreatives
        .filter(
          (c) =>
            c.creativeType === "makeover" &&
            (c.listingId === listingId || c.listingIds?.includes(listingId)) &&
            c.status !== "deleted" &&
            c.status !== "overwritten-by-regeneration"
        )
        .sort((a, b) => (b.createdTime?.getTime?.() ?? 0) - (a.createdTime?.getTime?.() ?? 0)),
    [allCreatives, listingId]
  );

  const completedMakeovers = useMemo(
    () => listingMakeovers.filter((m) => m.status === "completed" && !!m.generatedCreativeUrl),
    [listingMakeovers]
  );
  const hasCompletedMakeovers = completedMakeovers.length > 0;

  // Coins gating: each makeover image costs `makeoverCost` tokens (engine × quality, getMakeoverCoinCost).
  // Balance is null while loading — don't block the user until we actually know it.
  const balanceTotal = balance?.total ?? null;
  const selectionCost = selected.length * makeoverCost;
  const cannotAffordAny = balanceTotal != null && balanceTotal < makeoverCost;
  const insufficientForSelection = balanceTotal != null && balanceTotal < selectionCost;

  // Initialise the minisite-showcase switch from the server ONCE per listing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let active = true;
    if (!email || !listingId) return;
    getWebotListingMakeovers(email, [listingId])
      .then((res) => {
        if (active) setShowcaseOn((res.makeoversByListingId?.[listingId]?.length ?? 0) > 0);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [email, listingId]);

  const toggleSelect = (url: string) => {
    setError(null);
    setSelected((prev) => {
      if (prev.includes(url)) return prev.filter((u) => u !== url);
      if (prev.length >= MAX_SELECTION) return prev;
      return [...prev, url];
    });
  };

  const toggleStyle = (key: string) =>
    setSelectedStyleKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const buildStylePrompt = (): string | undefined => {
    const parts = STYLE_PRESETS.filter((p) => selectedStyleKeys.includes(p.key)).map((p) => p.stylePrompt);
    if (customActive && customStyle.trim()) parts.push(customStyle.trim());
    return parts.length ? parts.join(", ") : undefined;
  };

  const handleGenerate = async () => {
    if (!selected.length || isGenerating) return;
    if (insufficientForSelection) {
      setError(
        translate(
          "makeovers.insufficientCoins",
          "אין מספיק טוקנים. נדרשים {{required}} טוקנים ל-{{count}} תמונות, ובמאגר יש {{available}}.",
          { required: selectionCost, count: selected.length, available: balanceTotal ?? 0 }
        )
      );
      return;
    }

    const stylePrompt = buildStylePrompt();
    const sceneType: MakeoverSceneType = "auto";
    const request: REST_schema.GenerateMakeoversRequest = {
      listingId,
      items: selected.map((sourceImageUrl) => ({
        sourceImageUrl,
        sceneType,
        intensity,
        stylePrompt,
        additionalNotes: additionalNotes.trim() || undefined,
        engine,
        quality
      }))
    };

    setIsGenerating(true);
    setError(null);
    try {
      const res = await generateMakeovers(request);
      if (res.errorCode === "insufficient_coins") {
        setError(
          translate(
            "makeovers.insufficientCoins",
            "אין מספיק טוקנים. נדרשים {{required}} טוקנים ל-{{count}} תמונות, ובמאגר יש {{available}}.",
            {
              required: res.coinsRequired ?? selectionCost,
              count: selected.length,
              available: res.coinsAvailable ?? balanceTotal ?? 0
            }
          )
        );
      } else if (!res.success && res.errorMessage) {
        setError(res.errorMessage);
      } else {
        setSelected([]);
        setShowcaseOn(true); // a freshly-generated batch is auto-showcased by the backend
      }
    } catch (e: any) {
      setError(e?.message || translate("makeovers.genericError", "אירעה שגיאה ביצירת העיצובים"));
    } finally {
      setIsGenerating(false);
      refetchBalance(); // tokens were spent (failed images are refunded server-side) — refresh
    }
  };

  const handleShowcaseToggle = async (checked: boolean) => {
    setShowcaseOn(checked);
    setShowcaseBusy(true);
    try {
      await setMakeoverShowcase({ listingId, enabled: checked });
    } catch {
      setShowcaseOn(!checked); // revert on failure
    } finally {
      setShowcaseBusy(false);
    }
  };

  const handleShareWithOrgToggle = async (checked: boolean) => {
    setShareWithOrg(checked);
    setShowcaseBusy(true);
    try {
      await setMakeoverShowcase({ listingId, shareWithOrg: checked });
    } catch {
      setShareWithOrg(!checked); // revert on failure (e.g. not an org admin)
    } finally {
      setShowcaseBusy(false);
    }
  };

  const handleDownload = async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `makeover-${new Date().toISOString()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    }
  };

  const resultPairs: ComparisonPair[] = completedMakeovers.map((m) => ({
    beforeUrl: m.sourceImageUrl ?? m.usedImageUrls?.[0] ?? "",
    afterUrl: m.generatedCreativeUrl
  }));
  const openResultGallery = (makeover: GeneratedUserCreative) => {
    const index = completedMakeovers.findIndex((m) => m.id === makeover.id);
    setResultDialog({ open: true, index: Math.max(0, index) });
  };

  return (
    <Stack gap={3}>
      {showBalance && <CoinBalanceDisplay balance={balance} loading={balanceLoading} />}

      {/* ---- Design form ---- */}
      <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Stack gap={3}>
      {/* Step 1 — pick photos */}
      <Stack gap={1.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {translate("makeovers.pickImages", "בחרו עד 5 תמונות לעיצוב")}
          {selected.length > 0 ? ` (${selected.length}/${MAX_SELECTION})` : ""}
        </Typography>
        {images.length === 0 ? (
          <Typography color="text.secondary">{translate("makeovers.noImages", "אין תמונות זמינות לנכס זה")}</Typography>
        ) : (
          <Grid container spacing={1.5}>
            {images.map((url, idx) => {
              const isSelected = selected.includes(url);
              const disabled = !isSelected && selected.length >= MAX_SELECTION;
              return (
                <Grid item xs={4} sm={3} md={2} key={url}>
                  <Box
                    onClick={() => !disabled && toggleSelect(url)}
                    sx={{
                      position: "relative",
                      width: "100%",
                      paddingTop: "100%",
                      borderRadius: 1.5,
                      overflow: "hidden",
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.5 : 1,
                      outline: (theme) => (isSelected ? `3px solid ${theme.palette.primary.main}` : "1px solid rgba(0,0,0,0.08)")
                    }}
                  >
                    <Box component="img" src={url} alt="" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                    {isSelected && (
                      <Box sx={{ position: "absolute", top: 4, insetInlineStart: 4, backgroundColor: "common.white", borderRadius: "50%", lineHeight: 0 }}>
                        <CheckIcon fontSize="small" color="primary" />
                      </Box>
                    )}
                    <Tooltip title={translate("makeovers.viewImage", "הגדלה")}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSourceLightbox({ open: true, index: idx });
                        }}
                        sx={{ position: "absolute", bottom: 4, insetInlineEnd: 4, p: 0.25, backgroundColor: "rgba(255,255,255,0.85)", "&:hover": { backgroundColor: "common.white" } }}
                      >
                        <ZoomInIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Stack>

      <Divider />

      {/* Step 2 — how much to change */}
      <MakeoverIntensitySelector value={intensity} onChange={(v) => updatePrefs({ intensity: v })} />

      <Divider />

      {/* Step 3 — choose styles (multi-select + custom) */}
      <Stack gap={1.25}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {translate("makeovers.chooseStyle", "סגנון העיצוב (אפשר לבחור כמה)")}
        </Typography>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {STYLE_PRESETS.map((preset) => {
            const isOn = selectedStyleKeys.includes(preset.key);
            return (
              <Chip
                key={preset.key}
                label={translate(preset.labelKey, preset.labelDefault)}
                clickable
                color={isOn ? "primary" : "default"}
                variant={isOn ? "filled" : "outlined"}
                onClick={() => toggleStyle(preset.key)}
              />
            );
          })}
          <Chip
            label={translate("makeovers.style.custom", "מותאם אישית")}
            clickable
            color={customActive ? "primary" : "default"}
            variant={customActive ? "filled" : "outlined"}
            onClick={() => setCustomActive((v) => !v)}
          />
        </Stack>
        {customActive && (
          <TextField
            fullWidth
            size="small"
            value={customStyle}
            onChange={(e) => setCustomStyle(e.target.value)}
            placeholder={translate("makeovers.customStylePlaceholder", "תארו את הסגנון הרצוי, למשל: בוהו שיק עם צמחייה")}
            sx={{ maxWidth: 520 }}
          />
        )}
        {/* Additional notes — free-text specific instructions on what to change */}
        <Stack gap={0.5} sx={{ mt: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {translate("makeovers.additionalNotes", "הערות נוספות (אופציונלי)")}
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            placeholder={translate(
              "makeovers.additionalNotesPlaceholder",
              "הנחיות ספציפיות למה לשנות, למשל: להחליף את הספה לאפורה ולהוסיף צמחייה"
            )}
            sx={{ maxWidth: 520 }}
          />
        </Stack>
      </Stack>

      <Divider />

      {/* Step 4 — engine + quality (just before the CTA) */}
      <MakeoverModelSelector
        engine={engine}
        quality={quality}
        onEngineChange={(v) => updatePrefs({ engine: v })}
        onQualityChange={(v) => updatePrefs({ quality: v })}
      />

      <Divider />

      {/* Step 5 — generate */}
      <Stack gap={0.75} alignItems="flex-start">
        <Button
          variant="contained"
          color="primary"
          startIcon={isGenerating ? <CircularProgress size={18} color="inherit" /> : <MakeoverIcon />}
          disabled={isGenerating || selected.length === 0 || insufficientForSelection}
          onClick={handleGenerate}
        >
          {isGenerating ? translate("makeovers.generatingButton", "יוצר עיצובים...") : translate("makeovers.generateButton", "צרו עיצוב AI")}
        </Button>
        {/* One line under the CTA: per-image price · current balance */}
        <Typography variant="caption" color={insufficientForSelection || cannotAffordAny ? "error.main" : "text.secondary"}>
          {translate("makeovers.costPerImageMany", "כל עיצוב עולה {{cost}} טוקנים 🪙", { cost: makeoverCost })}
          {selected.length > 0 && ` · ${translate("makeovers.selectionTotal", "סך הכל {{total}}", { total: selectionCost })}`}
          {balanceTotal != null && ` · ${translate("makeovers.balanceShort", "יתרה: {{balance}}", { balance: balanceTotal })}`}
        </Typography>
      </Stack>

      {error && (
        <Typography variant="body2" sx={{ color: "error.main" }}>
          {error}
        </Typography>
      )}
        </Stack>
      </Card>

      {/* Results */}
      {listingMakeovers.length > 0 && (
        <Stack gap={1.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            {/* Listing title (clickable → opens listing details), same as the "previous makeovers" groups */}
            <ListingTitleLink listingId={listingId} count={listingMakeovers.length} />
            {hasCompletedMakeovers && (
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <FormControlLabel
                  control={<Switch checked={showcaseOn} disabled={showcaseBusy} onChange={(e) => handleShowcaseToggle(e.target.checked)} color="primary" />}
                  label={translate("makeovers.showOnMinisite", "הצג במיני-סייט")}
                />
                {isAdminOfTheOrganization && (
                  <FormControlLabel
                    control={<Checkbox checked={shareWithOrg} disabled={showcaseBusy} onChange={(e) => handleShareWithOrgToggle(e.target.checked)} color="primary" />}
                    label={translate("makeovers.shareWithOrg", "שיתוף תמונות ברמת המשרד")}
                  />
                )}
              </Stack>
            )}
          </Stack>
          <Grid container spacing={3}>
            {listingMakeovers.map((makeover) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={makeover.id}>
                <MakeoverCard makeover={makeover} onDownload={handleDownload} onExpand={openResultGallery} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      )}

      {/* Source images — plain gallery */}
      <LightboxModal
        images={images}
        isOpen={sourceLightbox.open}
        photoIndex={sourceLightbox.index}
        setPhotoIndex={(index) => setSourceLightbox((s) => ({ ...s, index }))}
        onClose={() => setSourceLightbox((s) => ({ ...s, open: false }))}
      />
      {/* Results — before/after comparison modal (with retry/delete on the dashboard) */}
      <MakeoverComparisonDialog
        open={resultDialog.open}
        pairs={resultPairs}
        makeovers={completedMakeovers}
        index={resultDialog.index}
        onClose={() => setResultDialog((s) => ({ ...s, open: false }))}
        onIndexChange={(index) => setResultDialog((s) => ({ ...s, index }))}
      />
    </Stack>
  );
}

export default MakeoverStudio;
